/**
 * /learn/courses/:courseId/done — course-completion celebration.
 *
 * Shown after the learner finishes the last module. Reads the freshest
 * `course.progress.bucket` from the cache; defensively redirects back to the
 * course detail if the course isn't actually completed (e.g., the learner
 * deep-linked here).
 *
 * Visual matches Figma 9198:279122 — cinematic gradient frame, mascot /
 * trophy illustration, score chip, and dual CTAs (Back to courses /
 * Browse certificates).
 */

import React, { useEffect, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { CheckBadgeIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { EASE_OUT } from '@/lib/motion';
import { useCourse } from '../../hooks/learner/useCourse';
import { useCourseRoutes } from '../../hooks/learner/useCourseRoutes';
import { useProgramJourney } from '../../hooks/learner/useProgramDays';

const CourseFinishPage: React.FC = () => {
  const { assignmentId } = useParams<{ assignmentId?: string }>();
  const id = assignmentId ?? '';
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();
  const routes = useCourseRoutes(id);
  const { data: course, isLoading } = useCourse(id);

  // What this course actually awards, so the closing screen stops promising a
  // certificate it never issues (Aleix, 2026-09-10: the button was always
  // there, and on a course without certification it just showed OTHER
  // courses' certificates).
  const certification = (course as { certification_type?: string | null } | undefined)
    ?.certification_type;
  const programId =
    (course as { assignment?: { program_id?: string | null } } | undefined)?.assignment
      ?.program_id ?? null;
  const inProgram = Boolean(programId);

  // A course inside a program is a chapter, not a destination: when one ends
  // the learner is handed the next one, and only the LAST one closes the
  // program (Aleix, 2026-09-10).
  const { data: journey } = useProgramJourney(programId ?? undefined);
  const orderedCourses = useMemo(() => {
    if (!journey) return [];
    return [...journey.days]
      .sort((a, b) => a.day_number - b.day_number)
      .flatMap((day) =>
        [...(day.courses ?? [])].sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0))
      );
  }, [journey]);

  const currentIndex = orderedCourses.findIndex(
    (c) => c.assignment_id === id || c.course_id === course?.id
  );
  const nextChapter =
    currentIndex >= 0
      ? orderedCourses.slice(currentIndex + 1).find((c) => !c.completed && c.assignment_id)
      : orderedCourses.find((c) => !c.completed && c.assignment_id);
  const programDone = Boolean(journey?.program?.completed) || (inProgram && !nextChapter);
  const programHasCertificate =
    !!journey?.program?.certification_type && journey.program.certification_type !== 'none';
  // A course inside a program is certified by the program, not by itself.
  const hasCertificate = !inProgram && !!certification && certification !== 'none';

  // Defensive guard: if the course isn't actually completed, route back to
  // the detail page. The cursor logic on CourseDetailPage will pick the
  // right module for "Resume" so the learner never sits on a dead /done.
  useEffect(() => {
    if (isLoading || !course || !id) return;
    if (course.progress?.bucket !== 'completed') {
      navigate(`${routes.base}`, { replace: true });
    }
  }, [isLoading, course, id, navigate, routes.base]);

  if (isLoading || !course) {
    return (
      <div className="max-w-[760px] mx-auto px-4 sm:px-6 py-12 space-y-6 animate-pulse">
        <div className="h-6 w-40 mx-auto rounded bg-text-tertiary/10" />
        <div className="aspect-[16/10] rounded-[28px] bg-text-tertiary/10" />
        <div className="h-12 w-2/3 mx-auto rounded bg-text-tertiary/10" />
      </div>
    );
  }

  const completed = course.progress?.modules_completed ?? 0;
  const total = course.progress?.modules_total ?? 0;
  const percent = course.progress?.progress_percent ?? 0;

  return (
    <motion.div
      initial={reduceMotion ? false : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: EASE_OUT }}
      className="max-w-[760px] mx-auto px-4 sm:px-6 py-10 space-y-7"
    >
      <Link
        to={routes.coursesIndex}
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-text-secondary hover:text-text-primary transition-colors"
      >
        <ArrowLeftIcon className="w-4 h-4" />
        Back to My Courses
      </Link>

      {/* Cinematic frame */}
      <section
        className="relative rounded-[28px] p-[10px] sm:p-3"
        style={{
          background:
            'linear-gradient(to right, rgb(var(--color-primary) / 0.22), rgb(var(--color-secondary) / 0.22))',
        }}
      >
        <div
          className="relative overflow-hidden rounded-[20px] aspect-[16/10] sm:aspect-[21/10] min-h-[280px]"
          style={{ boxShadow: '0 8px 28px rgba(0,0,0,0.18)' }}
        >
          <div
            aria-hidden="true"
            className="absolute inset-0"
            style={{
              background:
                'radial-gradient(circle at 50% 35%, rgb(var(--color-primary-400)) 0%, rgb(var(--color-primary-600)) 45%, rgb(var(--color-primary-950)) 100%)',
            }}
          />
          <div
            aria-hidden="true"
            className="absolute inset-0"
            style={{
              background:
                'radial-gradient(circle at 50% 50%, transparent 60%, rgba(0,0,0,0.32) 100%)',
            }}
          />

          <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6 py-8 gap-5">
            <motion.div
              initial={reduceMotion ? false : { opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.45, ease: EASE_OUT, delay: reduceMotion ? 0 : 0.1 }}
              className="inline-flex items-center justify-center w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-white/15 backdrop-blur-sm border border-white/20"
              style={{ boxShadow: '0 12px 36px rgba(0,0,0,0.32)' }}
            >
              <CheckBadgeIcon className="w-14 h-14 sm:w-16 sm:h-16 text-white" />
            </motion.div>

            <div className="space-y-2 max-w-[60ch]">
              <p className="text-[11px] sm:text-xs font-bold text-white/70 uppercase tracking-[0.18em]">
                {inProgram
                  ? programDone
                    ? 'Program completed'
                    : `Part ${currentIndex >= 0 ? currentIndex + 1 : 1} of ${orderedCourses.length || 1}`
                  : 'Course completed'}
              </p>
              <h1 className="text-2xl sm:text-3xl font-extrabold text-white leading-tight tracking-tight">
                {inProgram && programDone
                  ? `You finished ${journey?.program?.name ?? 'the program'}!`
                  : `You finished ${course.title ?? 'the course'}!`}
              </h1>
              <p className="text-sm text-white/85 leading-relaxed">
                {inProgram
                  ? programDone
                    ? programHasCertificate
                      ? 'That was the last part — your certificate is ready.'
                      : 'That was the last part of the program. Well done.'
                    : 'One part down — the next one is ready when you are.'
                  : 'Nice work — every module is checked off.'}
              </p>
            </div>

            <ScoreChip completed={completed} total={total} percent={percent} />
          </div>
        </div>
      </section>

      {/* Action row */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3">
        <button
          type="button"
          onClick={() =>
            navigate(inProgram && programId ? `/learn/programs/${programId}` : routes.coursesIndex)
          }
          className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold text-primary-600 dark:text-primary-300 border border-primary-500/40 hover:bg-primary-500/[0.06] active:scale-[0.97] transition-[background-color,transform] duration-150"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          {inProgram ? 'Back to the program' : 'Back to My Courses'}
        </button>
        {inProgram && !programDone && nextChapter && (
          <button
            type="button"
            onClick={() => navigate(`/learn/courses/${nextChapter.assignment_id}`)}
            className="inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 hover:bg-primary-600 dark:hover:bg-primary-400 text-sm font-bold active:scale-[0.97] transition-[background-color,transform] duration-150"
          >
            Continue to {nextChapter.course?.title ?? 'the next part'}
          </button>
        )}

        {inProgram && programDone && programHasCertificate && (
          <button
            type="button"
            onClick={() => navigate('/learn/certificates')}
            className="inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 hover:bg-primary-600 dark:hover:bg-primary-400 text-sm font-bold active:scale-[0.97] transition-[background-color,transform] duration-150"
          >
            <CheckBadgeIcon className="w-4 h-4" />
            View certificate
          </button>
        )}

        {hasCertificate && (
          <button
            type="button"
            onClick={() => navigate('/learn/certificates')}
            className="inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-full text-sm font-bold bg-primary-500 hover:bg-primary-600 dark:hover:bg-primary-400 active:scale-[0.97] transition-[background-color,transform] duration-150"
          >
            <CheckBadgeIcon className="w-4 h-4" />
            View certificate
          </button>
        )}
      </div>

      {!hasCertificate && (!inProgram || (programDone && !programHasCertificate)) && (
        <p className="text-center text-sm text-text-secondary">
          {inProgram
            ? "This program doesn't include a certificate."
            : "This course doesn't include a certificate."}
        </p>
      )}
    </motion.div>
  );
};

export default CourseFinishPage;

interface ScoreChipProps {
  completed: number;
  total: number;
  percent: number;
}

function ScoreChip({ completed, total, percent }: ScoreChipProps) {
  return (
    <span className="relative inline-flex items-center gap-2 px-3 py-1.5 rounded-full overflow-hidden">
      <span aria-hidden="true" className="absolute inset-0 bg-white mix-blend-overlay rounded-full" />
      <span aria-hidden="true" className="absolute inset-0 bg-white/55 rounded-full" />
      <span className="relative text-xs font-semibold text-grey-900">
        {completed}/{total} modules · {Math.round(percent)}%
      </span>
    </span>
  );
}
